import { useEffect, useMemo, useState } from "react";
import {
    LiveKitRoom,
    RoomAudioRenderer,
    useTracks,
    useRemoteParticipants,
    useLocalParticipant,
    useRoomContext,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { api } from "../lib/api";
import { Broadcast, WarningCircle } from "@phosphor-icons/react";

/**
 * LiveKitStage
 * - Host (canPublish=true): publishes camera + mic
 * - Guest: publishes camera + mic when on stage
 * - Viewer: subscribes only
 *
 * Surfaces real connection / device errors instead of hanging on
 * "Starting camera…" forever (e.g. DuckDuckGo / privacy browsers
 * blocking getUserMedia).
 */
export default function LiveKitStage({ liveId, enabled, fallback, shouldPublish, authorName }) {
    const [tok, setTok] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!enabled || !liveId) return;
        let cancelled = false;
        api.get(`/live/${liveId}/token`)
            .then(({ data }) => {
                if (cancelled) return;
                if (!data?.url || !data?.token) throw new Error("invalid token response");
                setTok(data);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e.response?.data?.detail || "Live streaming unavailable");
            });
        return () => { cancelled = true; };
    }, [enabled, liveId]);

    if (!enabled) return fallback || null;
    if (error) {
        // Backend not configured (503) or other error → show parent's fallback UI
        return fallback || (
            <div className="absolute inset-0 flex items-center justify-center text-white/50">
                <div className="text-center">
                    <Broadcast size={56} weight="fill" className="text-wta-primary mx-auto" />
                    <div className="font-heading text-3xl tracking-wide mt-3 text-white/80">@{authorName}</div>
                    <div className="text-xs tracking-[0.3em] uppercase text-wta-muted mt-1">Streaming live</div>
                </div>
            </div>
        );
    }
    if (!tok) {
        return (
            <div className="absolute inset-0 flex items-center justify-center text-white/40">
                <div className="text-xs uppercase tracking-[0.3em]">Connecting…</div>
            </div>
        );
    }

    return (
        <LiveKitRoom
            serverUrl={tok.url}
            token={tok.token}
            connect={true}
            // We DON'T auto-publish here. PublishController below enables camera/mic
            // manually so we can capture and surface errors (permission denied,
            // device-in-use, browser unsupported) instead of hanging forever.
            video={false}
            audio={false}
            onError={(err) => {
                console.error("[LiveKit] room error:", err);
                setError(err?.message || "Live connection error");
            }}
            onMediaDeviceFailure={(failure) => {
                console.error("[LiveKit] media device failure:", failure);
            }}
            data-lk-theme="default"
            className="absolute inset-0"
        >
            {shouldPublish && <PublishController />}
            <StageContent shouldPublish={shouldPublish} authorName={authorName} />
            <RoomAudioRenderer />
        </LiveKitRoom>
    );
}

/**
 * Manually enables camera + mic on the local participant once the room is
 * connected. Surfaces real getUserMedia / permission errors that the
 * `<LiveKitRoom video audio>` auto-publish would swallow silently.
 */
function PublishController() {
    const room = useRoomContext();
    const { localParticipant } = useLocalParticipant();
    const [pubError, setPubError] = useState(null);

    useEffect(() => {
        if (!room || !localParticipant) return;
        let cancelled = false;
        const enable = async () => {
            try {
                // Wait for room to actually be connected before publishing.
                if (room.state !== "connected") {
                    await new Promise((resolve) => {
                        const onConnected = () => { room.off("connected", onConnected); resolve(); };
                        room.on("connected", onConnected);
                        // safety: also resolve if already connected by the time listener attaches
                        if (room.state === "connected") { room.off("connected", onConnected); resolve(); }
                    });
                }
                if (cancelled) return;
                // Enable camera + mic. Errors propagate (permission denied, no device, etc.)
                await localParticipant.setCameraEnabled(true);
                if (cancelled) return;
                await localParticipant.setMicrophoneEnabled(true);
            } catch (err) {
                console.error("[LiveKit] failed to publish camera/mic:", err);
                if (cancelled) return;
                let msg = err?.message || String(err);
                if (err?.name === "NotAllowedError" || /permission/i.test(msg)) {
                    msg = "Camera/microphone permission denied. Allow camera access in your browser and reload.";
                } else if (err?.name === "NotFoundError" || /not found|no device/i.test(msg)) {
                    msg = "No camera detected on this device.";
                } else if (err?.name === "NotReadableError" || /in use|busy/i.test(msg)) {
                    msg = "Camera is in use by another app. Close other apps and try again.";
                } else if (/secure context|https/i.test(msg)) {
                    msg = "Camera requires HTTPS. Open the site over https:// and try again.";
                } else if (!navigator.mediaDevices?.getUserMedia) {
                    msg = "Your browser does not support camera capture. Try Chrome or Safari.";
                }
                setPubError(msg);
            }
        };
        enable();
        return () => { cancelled = true; };
    }, [room, localParticipant]);

    if (!pubError) return null;
    return (
        <div className="absolute inset-x-0 top-20 z-30 flex justify-center pointer-events-none px-4">
            <div data-testid="livekit-camera-error" className="pointer-events-auto bg-wta-surface border border-wta-primary rounded-sm px-4 py-3 max-w-sm text-xs flex items-start gap-2">
                <WarningCircle size={18} weight="fill" className="text-wta-primary shrink-0 mt-[1px]" />
                <div className="text-white/90">{pubError}</div>
            </div>
        </div>
    );
}

function StageContent({ shouldPublish, authorName }) {
    const { localParticipant } = useLocalParticipant();
    const remotes = useRemoteParticipants();
    // Pull all subscribed camera tracks (remote) + local camera if publishing
    const tracks = useTracks(
        [{ source: Track.Source.Camera, withPlaceholder: false }],
        { onlySubscribed: false }
    );

    // Pick the "stage" video: prefer a publisher whose metadata.role === "host"
    const stageTrack = useMemo(() => {
        const isHost = (p) => {
            try { return JSON.parse(p?.metadata || "{}").role === "host"; }
            catch { return false; }
        };
        const hostTrack = tracks.find((t) => isHost(t.participant));
        if (hostTrack) return hostTrack;
        // Fall back to first remote camera track
        return tracks[0] || null;
    }, [tracks]);

    if (!stageTrack) {
        // No publisher yet → host-side: their local camera is publishing on next render
        // Viewer-side: stream may have just started, show "waiting" placeholder
        return (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                    <Broadcast size={56} weight="fill" className="text-wta-primary mx-auto" />
                    <div className="font-heading text-3xl tracking-wide mt-3 text-white/80">@{authorName}</div>
                    <div className="text-xs tracking-[0.3em] uppercase text-wta-muted mt-1">
                        {shouldPublish ? "Starting camera…" : "Waiting for host…"}
                    </div>
                </div>
            </div>
        );
    }

    // Render the chosen video track. The publication's `track` has attach() returning
    // an HTMLMediaElement we can mount via ref-callback.
    return <VideoTile trackRef={stageTrack} mirror={localParticipant?.identity === stageTrack.participant.identity} />;
}

function VideoTile({ trackRef, mirror }) {
    return (
        <video
            ref={(el) => {
                if (!el) return;
                const pub = trackRef.publication;
                if (pub?.track) {
                    pub.track.attach(el);
                }
            }}
            autoPlay
            playsInline
            muted={mirror}  // mute self-view to avoid echo
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: mirror ? "scaleX(-1)" : "none" }}
        />
    );
}
